import type { Point, PointArray } from '../types/contour-definition-array-type';

import { getPolygonAreaAndCentroid } from './getPolygonAreaAndCentroid';

/**
 * Scales a contour (grows or shrinks) by a given percentage relative to its centroid.
 * Positive percent grows the contour (e.g. +5%), negative percent shrinks it (e.g. -5%).
 *
 * @param pointArray - The input contour points (scaled / image-pixel coordinates)
 * @param centroid - Optional center point for scaling. If omitted or invalid, computed from polygon.
 * @param percentChange - Percent change to scale by (default: 5, e.g. +5 or -5)
 * @returns Scaled contour points
 */
export function getScaleContour(
  pointArray: PointArray,
  centroid?: Point,
  percentChange: number = 5,
): PointArray {
  if (!pointArray || pointArray.length < 2) return pointArray;

  const isClosed
    = pointArray.length > 0
      && pointArray[0].x === pointArray[pointArray.length - 1].x
      && pointArray[0].y === pointArray[pointArray.length - 1].y;

  const len = isClosed ? pointArray.length - 1 : pointArray.length;
  const points = pointArray.slice(0, len);
  if (points.length === 0) return pointArray;

  let center = centroid;
  if (
    !center
    || !Number.isFinite(center.x)
    || !Number.isFinite(center.y)
    || (center.x === -1 && center.y === -1)
  ) {
    const { centroid: calcCentroid } = getPolygonAreaAndCentroid(
      isClosed ? pointArray : [...pointArray, pointArray[0]],
    );
    if (
      Number.isFinite(calcCentroid.x)
      && Number.isFinite(calcCentroid.y)
      && (calcCentroid.x !== 0 || calcCentroid.y !== 0)
    ) {
      center = calcCentroid;
    }
    else {
      const sumX = points.reduce((acc, p) => acc + p.x, 0);
      const sumY = points.reduce((acc, p) => acc + p.y, 0);
      center = { x: sumX / points.length, y: sumY / points.length };
    }
  }

  const factor = 1 + percentChange / 100;

  // Guard against complete collapse or negative inversion
  if (factor <= 0.01) return pointArray;

  // If shrinking, guard against collapsing points onto the centroid
  if (percentChange < 0) {
    let maxDistSq = 0;
    for (let i = 0; i < points.length; i++) {
      const dx = points[i].x - center.x;
      const dy = points[i].y - center.y;
      const distSq = dx * dx + dy * dy;
      if (distSq > maxDistSq) maxDistSq = distSq;
    }
    // Furthest vertex is already within ~1 pixel of centroid; stop shrinking
    if (maxDistSq < 1.0) {
      return pointArray;
    }
  }

  const scaled = points.map(point => ({
    x: center.x + (point.x - center.x) * factor,
    y: center.y + (point.y - center.y) * factor,
  }));

  if (isClosed && scaled.length > 0) {
    scaled.push({ ...scaled[0] });
  }

  return scaled;
}

export const scaleContour = getScaleContour;
